import type { HTMLAttributes, Ref } from "react";

import { useCaseHead } from "./CaseHeadSlots";
import styles from "./PageTitle.module.css";

interface PageTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** The screen's own class — its spacing and, where no frame stands, its look. */
  className?: string;
  headingRef?: Ref<HTMLHeadingElement>;
  /**
   * Whether the h1 carries the screen's red mesh marker (default). `false`
   * where a kicker above it already carries it (`ScreenHead` with a label):
   * one red on a screen, not two.
   */
  marked?: boolean;
  /**
   * Keeps the screen's own title look in a frame too — the `h2` without the
   * section's rule. Only for the review of a reading, frozen in its layout
   * for the M2 measurement windows until 09.11.2026 (final review, I-1): its
   * 28-px display title sets the height of its sticky head, and through it
   * the height of the fields a reviewer measures against.
   */
  ownLook?: boolean;
}

/**
 * A screen's own title — „Roste planen", „Kalkulation", „Zeichnung lesen" —
 * at the level it has on the page it stands on (Task 9, B-4/B-14).
 *
 * In a Vorgang's frame the Vorgang is the page's `h1` (its number and title
 * in the head over every tab), and the screen's title is a section of it: an
 * `h2` in the role of a panel title, `--text-lg` at 600 in the UI face. On
 * its own — the register demo's board, the calculation without a Vorgang, a
 * screen rendered in a test — it stays the page's `h1` in the screen's own
 * look. Every attribute goes through, `tabIndex={-1}` and the ref the screen
 * puts the focus on when it opens among them.
 *
 * On its own it is the screen's title, and carries the screen's one red mesh
 * marker (Task 11, UX-Gutachten 1, B-11) — first, empty and `aria-hidden`,
 * so the heading's name stays its words. In a frame the Vorgang's kicker has
 * that red, and the section title carries none.
 */
export function PageTitle({ className, headingRef, marked = true, ownLook = false, children, ...rest }: PageTitleProps) {
  // Framed only where the frame draws its head: a Vorgang that is gone has
  // none, and the page still needs its `h1` (final review, M-15).
  const framed = useCaseHead()?.shown ?? false;
  if (framed) {
    return (
      <h2 ref={headingRef} className={[className, ownLook ? null : styles.section].filter(Boolean).join(" ")} {...rest}>
        {children}
      </h2>
    );
  }
  return (
    <h1 ref={headingRef} className={className} {...rest}>
      {marked ? <span className="marke marke-titel" aria-hidden="true" /> : null}
      {children}
    </h1>
  );
}
